import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  WarehouseItem,
  WarehouseMovementType,
  MOVEMENT_TYPE_LABELS,
  useCreateMovement,
} from '@/hooks/warehouse';

interface RegisterMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouseItem: WarehouseItem | null;
}

const MOVEMENT_TYPES: WarehouseMovementType[] = [
  'entrada_compra',
  'salida_propiedad',
  'ajuste_merma',
  'devolucion',
];

const RegisterMovementModal = ({ isOpen, onClose, warehouseItem }: RegisterMovementModalProps) => {
  const [movementType, setMovementType] = useState<WarehouseMovementType>('entrada_compra');
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [notes, setNotes] = useState('');

  const { mutate: createMovement, isPending } = useCreateMovement();

  // Reset form and pre-fill unit price when modal opens
  useEffect(() => {
    if (isOpen && warehouseItem) {
      setMovementType('entrada_compra');
      setQuantity('');
      setUnitPrice(warehouseItem.unit_price.toString());
      setNotes('');
    }
  }, [isOpen, warehouseItem]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!warehouseItem || !quantity || !unitPrice) return;

    createMovement(
      {
        warehouse_item_id: warehouseItem.id,
        movement_type: movementType,
        quantity: parseInt(quantity, 10),
        unit_price: parseFloat(unitPrice),
        notes: notes.trim() || null,
      },
      { onSuccess: onClose }
    );
  };

  const isSubtraction = movementType === 'salida_propiedad' || movementType === 'ajuste_merma';
  const maxQuantity = isSubtraction ? warehouseItem?.quantity || 0 : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Registrar Movimiento</DialogTitle>
            <DialogDescription>
              {warehouseItem?.product?.name} - Stock actual: {warehouseItem?.quantity}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="movementType">Tipo de Movimiento</Label>
              <Select
                value={movementType}
                onValueChange={(v: WarehouseMovementType) => setMovementType(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el tipo" />
                </SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {MOVEMENT_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="quantity">Cantidad</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  max={maxQuantity}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  required
                />
                {isSubtraction && maxQuantity !== undefined && (
                  <p className="text-xs text-muted-foreground">
                    Máximo disponible: {maxQuantity}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="unitPrice">Precio Unitario</Label>
                <Input
                  id="unitPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Registrando...' : 'Registrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterMovementModal;
